'use client';

import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { consumeAssistantWorkspaceHandoff, type AssistantWorkspaceHandoffPayload } from '@/app/Components/Assistant/pageContext';
import { AssistantWorkspace } from './workspace/AssistantWorkspace';

type HandoffState = {
  ready: boolean;
  token?: string;
  payload: AssistantWorkspaceHandoffPayload;
};

function AIAssistantPage() {
  const searchParams = useSearchParams();
  const handoffToken = searchParams.get('handoff');
  const consumedTokenRef = useRef<string | undefined>(undefined);
  const [handoff, setHandoff] = useState<HandoffState>(() => ({
    ready: !handoffToken,
    payload: {},
  }));

  useEffect(() => {
    if (!handoffToken) {
      setHandoff((current) => current.ready ? current : { ready: true, payload: {} });
      return;
    }
    if (consumedTokenRef.current === handoffToken) return;
    const payload = consumeAssistantWorkspaceHandoff(handoffToken) ?? {};
    consumedTokenRef.current = handoffToken;
    setHandoff({ ready: true, token: handoffToken, payload });
  }, [handoffToken]);

  if (!handoff.ready) {
    return (
      <div className="grid h-full min-h-0 place-items-center bg-[var(--ss-canvas)] px-6 pt-24 text-sm text-[var(--ss-text-muted)]">
        Preparing your private assistant context…
      </div>
    );
  }

  const context = handoff.payload;
  return (
    <AssistantWorkspace
      requestedThreadId={searchParams.get('threadId') ?? undefined}
      initialPrompt={context.initialPrompt}
      autoSend={context.autoSend}
      selectedScheduleId={context.selectedScheduleId}
      selectedDegreePlanId={context.selectedDegreePlanId}
      sourceRoute={context.sourceRoute}
      activeTerm={context.activeTerm}
      selectedCourseIds={context.selectedCourseIds}
      visibleSectionIds={context.visibleSectionIds}
      selectedEntity={context.selectedEntity}
      availablePageActions={context.availablePageActions}
      visibleView={context.visibleView}
    />
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><AIAssistantPage /></RouteSuspense>; }
